import React, { useEffect, useState } from 'react';
import './InvitePage.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { acceptInviteRequest, checkUserRegistrationStatus } from '@/api'; // Додано checkUserRegistrationStatus
import { accessTokenSelector } from '@/store/auth/authSlices';
import { useSelector } from 'react-redux';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import InviteSuccessModal from '@/components/tenant/InviteSuccessModal';
import { AcceptInviteResponse } from '@/types/entities';
import axios from 'axios';

const baseClass = 'InvitePage';

const InvitePage = () => {
  const { token } = useParams<{ token: string }>();
  const accessToken = useSelector(accessTokenSelector);
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [responseData, setResponseData] = useState<AcceptInviteResponse | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invite token is missing or invalid.');
      setIsLoading(false);
      return;
    }

    const handleAcceptInvite = async () => {
      try {
        const response = await acceptInviteRequest(token);
        // Успіх, якщо статус 201 Created
        if (response.status === 201) {
          setResponseData(response.data);
          setShowSuccessModal(true);
        }
      } catch (e: any) {
        // Обробка помилок 400/404
        if (axios.isAxiosError(e) && e.response) {
          setError(e.response.data.message || 'Failed to accept the invitation.');
        } else {
          // Інші помилки (мережа, тощо)
          setError('A network error occurred. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    const handleUnauthenticatedUser = async () => {
      try {
        const { data } = await checkUserRegistrationStatus(token);
        if (data.isUserRegistered) {
          // Користувач зареєстрований, перенаправляємо на сторінку входу
          navigate(`/login?inviteToken=${token}`);
        } else {
          // Користувач не зареєстрований, перенаправляємо на сторінку реєстрації
          navigate(`/register?inviteToken=${token}`);
        }
      } catch (e: any) {
        if (axios.isAxiosError(e) && e.response && e.response.status === 404) {
          setError('Invite token not found or is invalid.');
        } else {
          setError('An error occurred while checking registration status.');
        }
        setIsLoading(false);
      }
    };

    if (accessToken) {
      handleAcceptInvite();
    } else {
      handleUnauthenticatedUser();
    }
  }, [token, accessToken, navigate]);

  const handleGoToTenancy = () => {
    navigate('/tenancy');
  };

  // Рендер стану завантаження
  if (isLoading) {
    return (
      <div className={`${baseClass}--loading`}>
        <h2>Processing Your Invitation...</h2>
        <p>Please wait a moment.</p>
      </div>
    );
  }

  // Рендер стану помилки
  if (error) {
    return (
      <div className={`${baseClass}--container`}>
        <Card className={`${baseClass}--card`}>
          <Card.Header>
            <Card.Title>Invitation Error</Card.Title>
          </Card.Header>
          <Card.Body>
            <p>{error}</p>
            <div className="mt-24">
              <Button onClick={() => navigate('/')}>Go to Homepage</Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <InviteSuccessModal
      isOpen={showSuccessModal}
      onClose={handleGoToTenancy}
      responseData={responseData}
    />
  );
};

export default InvitePage;
